import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * Keeps the add-to-cart button disabled until the customer has chosen every
 * "choose first" option (e.g. the ring size). The variant picker renders those
 * options without a preselected value and marks their inputs with
 * `data-gate-option`; this element only mirrors that state onto the button.
 *
 * Once a choice is made the picker re-renders the section with the chosen
 * variant, the server no longer renders this element, and the theme's own
 * button state takes over again.
 *
 * @extends {Component<{}>}
 */
class VariantOptionGate extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** Availability of the current variant, mirrored from the theme's button state. */
  #variantAvailable = true;

  /** Set while we have replaced the add-to-cart label with our own prompt. */
  #labelOverridden = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#picker()?.addEventListener("change", this.#onChange, { signal });
    this.#section()?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    const button = this.#atcButton();
    if (button) this.#variantAvailable = !button.disabled;

    this.#syncRequiredMessage();
    this.#syncAtcDisabled();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = undefined;

    // Hand the button back to the theme when the gate is removed (section morph).
    const button = this.#atcButton();
    if (!button) return;
    button.classList.remove("option-gate-atc", "btn--awaiting-choice");
    if (this.#labelOverridden && this.#variantAvailable) {
      const original = button.dataset.addToCartText;
      if (original) this.#writeAtcLabel(button, original);
      button.disabled = false;
    }
    this.#labelOverridden = false;
  }

  /** @returns {HTMLElement | null} */
  #picker() {
    return this.closest("variant-picker");
  }

  /** @returns {Element | Document} */
  #section() {
    return this.closest(".shopify-section, dialog") ?? document;
  }

  /**
   * The add-to-cart submit button of this section's product form.
   * @returns {HTMLButtonElement | null}
   */
  #atcButton() {
    const button = this.#section().querySelector('product-form-component add-to-cart-component [ref="addToCartButton"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * The gated inputs (radios and selects) inside this picker.
   * @returns {(HTMLInputElement | HTMLSelectElement)[]}
   */
  #gatedInputs() {
    const picker = this.#picker();
    if (!picker) return [];
    return Array.from(picker.querySelectorAll("[data-gate-option]")).filter(
      (el) => el instanceof HTMLInputElement || el instanceof HTMLSelectElement
    );
  }

  /**
   * True when every gated option has a value.
   * @returns {boolean}
   */
  #hasSelection() {
    const inputs = this.#gatedInputs();
    if (!inputs.length) return true;

    /** @type {Map<string, boolean>} */
    const groups = new Map();
    for (const input of inputs) {
      const key = input.dataset.gateOption ?? input.name;
      const chosen =
        input instanceof HTMLSelectElement ? input.value !== "" : input.checked;
      groups.set(key, (groups.get(key) ?? false) || chosen);
    }
    return Array.from(groups.values()).every(Boolean);
  }

  #onChange = () => {
    this.#syncRequiredMessage();
    this.#syncAtcDisabled();
  };

  /**
   * ProductFormComponent re-enables the button in its own listener for this
   * same event; re-apply our state after all listeners have run.
   * @type {EventListener}
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ detail?: { resource?: { available?: boolean } } }} */ (event).detail;
    this.#variantAvailable = detail?.resource?.available ?? this.#variantAvailable;
    queueMicrotask(() => this.#syncAtcDisabled());
  };

  #syncAtcDisabled() {
    const button = this.#atcButton();
    if (!button) return;

    const hasSelection = this.#hasSelection();
    const awaitingChoice = !hasSelection && this.#variantAvailable;

    button.disabled = !hasSelection || !this.#variantAvailable;
    button.classList.add("option-gate-atc");
    button.classList.toggle("btn--awaiting-choice", awaitingChoice);

    this.#syncAtcLabel(button, awaitingChoice);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} awaitingChoice
   */
  #syncAtcLabel(button, awaitingChoice) {
    const label = this.dataset.awaitingLabel;
    if (!label) return;

    if (awaitingChoice) {
      this.#writeAtcLabel(button, label);
      this.#labelOverridden = true;
      return;
    }

    if (!this.#labelOverridden) return;
    this.#labelOverridden = false;
    if (!this.#variantAvailable) return;

    const original = button.dataset.addToCartText;
    if (original) this.#writeAtcLabel(button, original);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #writeAtcLabel(button, text) {
    for (const node of button.querySelectorAll(".add-to-cart-text__content, .btn__icon-text")) {
      node.textContent = text;
    }
    button.setAttribute("aria-label", text);
  }

  /**
   * The gated inputs are `required` and belong to the add-to-cart form, so the
   * browser blocks submission until a choice is made. Swap the default message
   * for the merchant-configured one while nothing is selected.
   */
  #syncRequiredMessage() {
    const message = this.dataset.requiredMessage ?? "";
    const hasSelection = this.#hasSelection();
    for (const input of this.#gatedInputs()) {
      input.setCustomValidity(hasSelection ? "" : message);
    }
  }
}

if (!customElements.get("variant-option-gate")) {
  customElements.define("variant-option-gate", VariantOptionGate);
}
